'use client'
import { useScrolled } from '@/lib/hooks/useScrolled'

// Progressive blur: stacked backdrop-filter layers with offset masks. Each
// layer blurs more but is masked to a narrower band near the top, so the
// cumulative blur ramps smoothly from strong (top) to zero (~100px down).
// Desktop only (lg+) — stacking four backdrop-filters re-blurs the band four
// times per scroll frame, which drops mid-range phones/tablets to ~80fps
// with visible jank.
const LAYERS = [
  // L1 (base): fades in the bottom half so the band ends in zero blur
  // instead of dropping from 2px → 0px at the edge (which read as a
  // visible seam between blurred content and crisp text below).
  { blur: 2, mask: 'black 0%, black 55%, transparent 100%' },
  { blur: 4, mask: 'black 0%, black 50%, transparent 90%' },
  { blur: 8, mask: 'black 0%, black 30%, transparent 65%' },
  { blur: 16, mask: 'black 0%, black 12.5%, transparent 40%' },
]

// Mobile/tablet fallback: one masked backdrop-filter. Keeps the soft top
// fade while staying at 120fps on touch devices (measured: 4 layers → 80fps
// w/ 8% janky frames; 1 layer → 120fps w/ 0%).
const MOBILE_LAYER = { blur: 8, mask: 'black 0%, black 30%, transparent 80%' }

/**
 * Fixed blur band pinned to the top of the viewport. Sits at z-40 — above page
 * content, below the z-50 navbar — so the navbar's logo/pill/CTA stay crisp
 * while content scrolling underneath dissolves into blur. Fades in on scroll.
 */
export default function ScrollBlur() {
  const scrolled = useScrolled(60)

  return (
    <div
      aria-hidden
      className="scroll-blur-band fixed top-0 inset-x-0 z-40 pointer-events-none"
      style={{ height: 120, opacity: scrolled ? 1 : 0, transition: 'opacity 300ms ease' }}
    >
      {/* Mobile/tablet: single cheap layer */}
      <div
        className="absolute inset-0 lg:hidden"
        style={{
          backdropFilter: `blur(${MOBILE_LAYER.blur}px)`,
          WebkitBackdropFilter: `blur(${MOBILE_LAYER.blur}px)`,
          maskImage: `linear-gradient(to bottom, ${MOBILE_LAYER.mask})`,
          WebkitMaskImage: `linear-gradient(to bottom, ${MOBILE_LAYER.mask})`,
        }}
      />
      {/* Desktop: full progressive stack */}
      {LAYERS.map((l) => (
        <div
          key={l.blur}
          className="absolute inset-0 hidden lg:block"
          style={{
            backdropFilter: `blur(${l.blur}px)`,
            WebkitBackdropFilter: `blur(${l.blur}px)`,
            maskImage: `linear-gradient(to bottom, ${l.mask})`,
            WebkitMaskImage: `linear-gradient(to bottom, ${l.mask})`,
          }}
        />
      ))}
    </div>
  )
}
